<template>
  <div class="remove-account">
    <b-overlay
      :show="loading"
      variant="transparent"
      rounded="sm"
    >
      <template #overlay>
        <loading-spinner />
      </template>

      <div class="ra-card">
        <div
          class="ra-card-glow"
          aria-hidden="true"
        />
        <div class="ra-head">
          <span class="ra-icon">
            <feather-icon
              icon="TrashIcon"
              size="20"
            />
          </span>
          <div>
            <h3 class="ra-title">
              {{ $t('Delete Account') }}
            </h3>
            <p>{{ $t('This action cannot be undone.') }}</p>
          </div>
        </div>

        <div class="ra-warning">
          <feather-icon
            icon="AlertTriangleIcon"
            size="20"
          />
          <p>{{ $t('pages.deleteAccount.description') }}</p>
        </div>

        <validation-observer
          v-if="reasons.length"
          ref="loginForm"
          #default="{}"
        >
          <b-form
            class="ra-form"
            @submit.prevent
          >
            <label
              for="reason"
              class="ra-label"
            >{{ $t('Account deleting reason') }}</label>
            <validation-provider
              #default="{ errors }"
              :name="$t('Account deleting reason')"
              vid="reason"
              rules="required"
            >
              <v-select
                id="reason"
                v-model="reason"
                class="ra-select"
                :clearable="false"
                :searchable="false"
                label="name"
                :options="reasons"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>

            <button
              type="button"
              class="ra-delete-btn"
              :disabled="!reason"
              @click="showRemoveModal"
            >
              <feather-icon
                icon="TrashIcon"
                size="16"
              />
              {{ $t('Delete My Account') }}
            </button>
          </b-form>
        </validation-observer>

        <div class="ra-info">
          <feather-icon
            icon="InfoIcon"
            size="18"
          />
          <p>{{ $t('pages.deleteAccount.information') }}</p>
        </div>
      </div>

      <b-modal
        v-model="removeModal"
        centered
        hide-header
        hide-footer
        size="md"
        modal-class="ra-modal"
      >
        <div class="ra-modal-icon">
          <feather-icon
            icon="AlertTriangleIcon"
            size="30"
          />
        </div>
        <h4 class="ra-modal-title">
          {{ $t('pages.deleteAccount.confirmTitle') }}
        </h4>
        <p class="ra-modal-text">
          {{ $t('pages.deleteAccount.confirmDescription') }}
        </p>
        <div class="ra-modal-actions">
          <button
            type="button"
            class="ra-btn-cancel"
            @click="hideRemoveModal"
          >
            {{ $t('Cancel') }}
          </button>
          <button
            type="button"
            class="ra-btn-delete"
            :disabled="loading"
            @click="remove"
          >
            {{ $t('Delete') }}
          </button>
        </div>
      </b-modal>
    </b-overlay>
  </div>
</template>

<script>
import { required } from '@validations'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { BButton, BCol, BForm, BFormCheckbox, BFormGroup, BFormInput, BOverlay, BRow } from 'bootstrap-vue'
import useJwt from '@/auth/jwt/useJwt'
import LoadingSpinner from '@/components/LoadingSpinner'
import { gtmTrackNewEvent, showNotify, throwDefaultError } from "@/helpers/helpers";
import vSelect from 'vue-select'

export default {
  components: {
    BButton,
    BCol,
    BForm,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BOverlay,
    BRow,

    LoadingSpinner,

    ValidationObserver,
    ValidationProvider,

    vSelect,
  },
  data() {
    return {
      loading: false,
      reason: null,
      reasons: [],
      removeModal: false,
    }
  },
  created() {
    this.getReasons()
  },
  methods: {
    getReasons() {
      useJwt.getReasons()
          .then(response => {
            this.reasons = response.data.body
          })
          .catch(error => {
            throwDefaultError(this.$toast, error)
          })
          .finally(() => { this.loading = false })
    },
    hideRemoveModal() {
      this.removeModal = false

      gtmTrackNewEvent('accountremovecancel', {
        category: 'HesapSilme',
        action: 'AccountRemoveCancel',
      })
    },
    remove() {
      if (this.reason && this.reason.id) {
        this.loading = true
        useJwt.removeAccount(this.reason.id)
            .then(response => {
              showNotify(this.$toast, this.$t('The account has been deleted'), 'success')

              gtmTrackNewEvent('accountremove', {
                category: 'HesapSilme',
                action: 'AccountRemove',
              })

              this.$router.push({name: 'auth-login'})
            })
            .catch(error => {
              throwDefaultError(this.$toast, error)
            })
            .finally(() => { this.loading = false })
      }
    },
    showRemoveModal() {
      this.removeModal = true

      gtmTrackNewEvent('accountremoveopen', {
        category: 'HesapSilme',
        action: 'AccountRemoveOpen',
      })
    },
  }
}
</script>

<style lang="scss" scoped>
.remove-account {
  margin: 0 -25px;
}

.ra-card {
  position: relative;
  overflow: hidden;
  background: rgba(32, 22, 72, 0.72);
  border: 1px solid rgba(150, 110, 255, 0.28);
  border-radius: 20px;
  box-shadow: 0 12px 40px rgba(6, 2, 24, 0.45);
  padding: 26px 30px 30px;

  @media (max-width: 575px) { padding: 20px 16px; }
}

.ra-card-glow {
  position: absolute;
  top: -60px;
  right: -60px;
  width: 280px;
  height: 280px;
  background: radial-gradient(circle, rgba(239, 68, 68, 0.22), transparent 70%);
  pointer-events: none;
}

.ra-head {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  padding-bottom: 18px;
  margin-bottom: 20px;
  border-bottom: 1px solid rgba(150, 110, 255, 0.2);

  p {
    color: rgba(226, 220, 255, 0.72);
    font-size: 13px;
    margin: 0;
  }
}

.ra-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: linear-gradient(135deg, #ef4444, #db2777);
  flex-shrink: 0;
}

.ra-title {
  color: #fff !important;
  font-size: 20px !important;
  font-weight: 700;
  margin: 0 0 2px;
}

.ra-warning,
.ra-info {
  position: relative;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px 16px;
  border-radius: 14px;
  max-width: 720px;

  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.6;
  }

  svg { flex-shrink: 0; margin-top: 1px; }
}

.ra-warning {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.35);
  color: #fecaca;

  svg { color: #f87171; }
}

.ra-info {
  margin-top: 22px;
  background: rgba(14, 10, 40, 0.45);
  border: 1px solid rgba(150, 110, 255, 0.2);
  color: rgba(236, 232, 255, 0.82);

  svg { color: #c4b5fd; }
}

.ra-form {
  position: relative;
  max-width: 460px;
  margin-top: 22px;
}

.ra-label {
  display: block;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 6px;
}

.ra-select {
  ::v-deep .vs__dropdown-toggle {
    min-height: 46px;
    background: rgba(14, 10, 40, 0.55);
    border: 1px solid rgba(170, 140, 255, 0.28);
    border-radius: 12px;
  }

  ::v-deep .vs__selected,
  ::v-deep .vs__search,
  ::v-deep .vs__placeholder { color: #fff; }

  ::v-deep .vs__open-indicator { fill: rgba(226, 220, 255, 0.7); }

  ::v-deep .vs__dropdown-menu {
    background: #1e1648;
    border: 1px solid rgba(170, 140, 255, 0.35);
    border-radius: 12px;
  }

  ::v-deep .vs__dropdown-option { color: #fff; white-space: normal; }
  ::v-deep .vs__dropdown-option--highlight { background: rgba(139, 92, 246, 0.45); }
}

.ra-delete-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 18px;
  padding: 12px 24px;
  border: 0;
  border-radius: 9999px;
  color: #fff;
  font-weight: 700;
  font-size: 15px;
  background: linear-gradient(135deg, #ef4444, #db2777);
  box-shadow: 0 8px 20px rgba(239, 68, 68, 0.35);
  cursor: pointer;

  &:hover:not(:disabled) { filter: brightness(1.1); }
  &:disabled { opacity: .45; cursor: not-allowed; box-shadow: none; }

  @media (max-width: 575px) { width: 100%; }
}
</style>

<style lang="scss">
.ra-modal {
  .modal-content {
    background: linear-gradient(160deg, #24175a 0%, #170f3d 100%);
    border: 1px solid rgba(239, 68, 68, 0.35);
    border-radius: 22px;
    box-shadow: 0 24px 60px rgba(4, 1, 20, 0.6);
    color: #f4f1ff;
  }

  .modal-body {
    padding: 28px 26px 24px;
    text-align: center;
  }

  .ra-modal-icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 14px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    background: linear-gradient(135deg, #ef4444, #db2777);
    box-shadow: 0 0 24px rgba(239, 68, 68, 0.45);
  }

  .ra-modal-title {
    color: #fff;
    font-size: 20px;
    font-weight: 700;
    margin-bottom: 8px;
  }

  .ra-modal-text {
    color: rgba(236, 232, 255, 0.8);
    font-size: 14px;
    line-height: 1.6;
    margin-bottom: 22px;
  }

  .ra-modal-actions {
    display: flex;
    gap: 12px;
    justify-content: center;

    button {
      flex: 1;
      max-width: 180px;
      padding: 11px 16px;
      border-radius: 9999px;
      font-weight: 700;
      cursor: pointer;
    }
  }

  .ra-btn-cancel {
    color: #fff;
    background: rgba(14, 10, 40, 0.6);
    border: 1px solid rgba(170, 140, 255, 0.35);
  }

  .ra-btn-delete {
    color: #fff;
    border: 0;
    background: linear-gradient(135deg, #ef4444, #db2777);
    box-shadow: 0 6px 18px rgba(239, 68, 68, 0.4);

    &:disabled { opacity: .6; }
  }
}
</style>
